var App = App || {};
App.Initialization = App.Initialization || {};

App.Initialization['component.formValidation'] = (function () {
    'use strict';

    // each initialized form will have its own state stored in this WeakMap, keyed by the form element
    const formStates = new WeakMap();

    // Initializes form validation for a given form
    const initialize = form => {
        formLoad(form);
        formUpdateSubmit(form);
    };

    // Loads form and attaches input validation listeners
    const formLoad = form => {
        const formInputs = form.querySelectorAll("[data-zag-validate-input]");
        formInputs.forEach(input => {
            input.addEventListener("input", () => validateInput(input));
        });
    };

    // Updates form submit behavior to use custom validation
    const formUpdateSubmit = form => {
        const submitBoundHandler = e => formCustomSubmit(e, form);
        formStates.set(form, { submitBoundHandler });

        // use capture phase so our validation runs before Kentico's handlers (reCAPTCHA, AJAX, etc.)
        form.addEventListener("submit", submitBoundHandler, true);
    };

    // Handles custom form submission with validation
    const formCustomSubmit = (e, form) => {
        const formState = formStates.get(form);
        if (!formState) return;

        // mark form as submitted
        form.setAttribute("data-zag-validate-form", "submitted");

        // validate each input
        const inputs = form.querySelectorAll("[data-zag-validate-input]");
        inputs.forEach(input => validateInput(input));

        // check for errors
        const errors = form.querySelectorAll('[data-zag-validate-field="error"]');
        if (errors.length > 0) {
            // block the event entirely so Kentico's handlers don't fire on an invalid form
            e.preventDefault();
            e.stopImmediatePropagation();
            const firstErrorInput = errors[0]?.querySelector("[data-zag-validate-input]");
            firstErrorInput?.focus();
            return;
        }

        // validation passed — remove our capture listener so we don't intercept again,
        // then let the event propagate naturally to Kentico's reCAPTCHA and AJAX handlers
        form.removeEventListener("submit", formState.submitBoundHandler, true);

        // disable submit button to prevent multiple submissions
        const submitButton = form.querySelector("input[type='submit'], button[type='submit']");
        if (submitButton) submitButton.disabled = true;

        // scroll to the top of the form (potentially for a confirmation message)
        const formTop = form.getBoundingClientRect().top + window.pageYOffset - 65;
        window.scrollTo({ top: formTop, behavior: 'smooth' });
    };

    // Validates a single input field
    const validateInput = input => {
        // find the field container
        const field = input.closest("[data-zag-validate-field]");
        if (!field) return;

        // get all error containers
        const errorsContainers = field.querySelectorAll("[data-zag-validate-errors]");
        errorsContainers.forEach(el => el.style.display = "block"); // make visible for validation

        // helper functions
        const show = el => el.style.display = "flex";
        const hide = el => el.style.display = "none";

        // get all error messages in this field
        const errorMessages = field.querySelectorAll("[data-zag-validate-error]");

        // required error
        const requiredErrors = Array.from(errorMessages).filter(el => el.dataset.zagValidateError === "required");

        const inputType = input.dataset.zagValidateInput;

        if (inputType === "text" || inputType === "upload") {
            requiredErrors.forEach(err => {
                input.value.trim() !== "" ? hide(err) : show(err);
            });
        }
        else if (inputType === "check") {
            const anyChecked = field.querySelectorAll('input[data-zag-validate-input="check"]:checked').length > 0;
            requiredErrors.forEach(err => anyChecked ? hide(err) : show(err));
        }
        else if (inputType === "recaptcha") {
            const recaptcha = field.querySelector(".g-recaptcha-response");
            const recaptchaResponse = recaptcha ? recaptcha.value : "";
            requiredErrors.forEach(err => recaptchaResponse !== "" ? hide(err) : show(err));
        }

        // maximum length
        const maxErrors = Array.from(errorMessages).filter(el => el.dataset.zagValidateError === "maximum");
        maxErrors.forEach(err => {
            const maxVal = parseMaxMin(err.dataset.zagValidateErrorValue);
            input.value.length <= maxVal ? hide(err) : show(err);
        });

        // minimum length
        const minErrors = Array.from(errorMessages).filter(el => el.dataset.zagValidateError === "minimum");
        minErrors.forEach(err => {
            const minVal = parseMaxMin(err.dataset.zagValidateErrorValue);
            input.value.length >= minVal ? hide(err) : show(err);
        });

        // regex validation
        const regexErrors = Array.from(errorMessages).filter(el => el.dataset.zagValidateError === "regex");
        regexErrors.forEach(err => {
            const pattern = new RegExp(err.dataset.zagValidateErrorValue);
            pattern.test(input.value) ? hide(err) : show(err);
        });

        // count number of visible errors in all error containers
        const visibleErrors = Array.from(errorMessages).filter(err => err.offsetParent !== null); // visible check
        if (visibleErrors.length > 0) {
            field.dataset.zagValidateField = "error";
            errorsContainers.forEach(el => el.style.display = "block");
        }
        else {
            field.dataset.zagValidateField = "";
            errorsContainers.forEach(el => el.style.display = "none");
        }
    };

    // Parses max/min value from string
    const parseMaxMin = str => {
        const num = parseFloat(str);
        return isNaN(num) ? 0 : num;
    };

    return {
        initialize
    };

})();